import { createFileRoute, redirect } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { trackEvent } from "@/lib/audit";

/**
 * CRITICAL SECURITY HOTFIX — Hidden SuperAdmin entry point.
 *
 * Previous behaviour (flipping a localStorage flag to unlock /admin) has been
 * removed — that was a client-side authorization bypass.
 *
 * The route now validates the request **server-side**:
 *   1. Requires an active Supabase auth session → otherwise → /login.
 *   2. Requires `superadmin`/`admin` role in `public.user_roles` (checked via
 *      the `is_superadmin` SECURITY DEFINER RPC) → otherwise → /tenant-portal.
 *
 * Only on success does it forward to /admin, where the PIN gate completes auth.
 */
export const Route = createFileRoute("/superadmin-access")({
  beforeLoad: async () => {
    if (typeof window === "undefined") return;
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData.session) {
      void trackEvent("tenant_isolation_enforced", {
        pathname: "/superadmin-access",
        reason: "no_session",
        enforced_by: "supabase_rbac",
      });
      throw redirect({ to: "/login", search: { from: undefined } });
    }
    const { data, error } = await supabase.rpc("is_superadmin");
    if (error || !data) {
      void trackEvent("tenant_isolation_enforced", {
        pathname: "/superadmin-access",
        reason: "not_super_admin",
        enforced_by: "supabase_rbac",
      });
      throw redirect({ to: "/tenant-portal", search: { tenant: undefined } });
    }
    void trackEvent("super_admin_access_verified", { source: "hidden_route_rbac" });
    throw redirect({ to: "/admin" });
  },
});